import { useMemo, useState } from 'react'
import { z } from 'zod'
import { Textarea } from '../../../ui/textarea'
import { cn } from '../../../../lib/utils'
import type {
  ConfirmationChannel, DurationUnit, EmployeeDto, EmployeeSkillType, ScheduleEventKind, ScheduleEventDto, SlotStartBody,
} from '../../../../lib/api/v2/planning'
import { slotStart, startTimeOf, isPlaceableTime, spillsPastMidnight, prefillForKind, sortEmployeeLanes } from '../planning-helpers'
import { START_TIME_STEP_SECONDS, isOutsideWorkDay, workDayLabel } from '../planning-settings'
import { NoEligibleEmployees } from '../NoEligibleEmployees'

/**
 * Inline action forms for the project detail sections (SOLY-PLAN-001 MS-24, design Round 4
 * §panneau détail + §Spécifique Métré/Pose). These replace the MS-22/23 `window.prompt()` calls:
 * confirmation client (canal + note), rapport (résumé), reasons (NotApplicable/Cancel, min 5 chars)
 * and estimate editing (Métré = heures step 0.5 · Pose = jours/heures step ¼ × nb placeurs, raison
 * optionnelle). Each form owns its local state, validates with Zod before calling onSubmit, and the
 * parent re-fetches the detail on success (onMutated wiring kept). Visual language follows the
 * existing inline forms (SupplierOrders / SchedulingFiche): native controls, sober borders.
 */

// 'event' is the agenda fiche (SOLY-PLAN-002): the same gesture, on a slot with no chantier behind it.
// 'pose-slot' is ONE créneau of a pose, distinct from 'pose' (the work item): both open a
// NotesForm, and sharing an area would emit `planning-pose-notes-*` twice as soon as two phases
// are expanded at once — a strict Playwright selector then matches two nodes (SOLY-PLAN-004).
type Area = 'metre' | 'pose' | 'metre-slot' | 'pose-slot' | 'event'

// ── Confirmation du créneau ─────────────────────────────────────────────────
/** FR label shown in the segmented control → BE ConfirmationChannel enum value. */
const CHANNELS: { label: string; value: ConfirmationChannel }[] = [
  { label: 'Téléphone', value: 'Phone' },
  { label: 'Email', value: 'Email' },
  { label: 'SMS', value: 'Sms' },
]

export function channelFromLabel(label: string): ConfirmationChannel {
  return CHANNELS.find((c) => c.label === label)?.value ?? 'Phone'
}

/**
 * FR display label for a recorded channel. One table for every surface that shows one — the drawer
 * and, since SOLY-PLAN-002, the agenda fiche. `Other` is displayable but not offerable: rows carry
 * it from before the segmented control existed.
 */
const CHANNEL_DISPLAY: Record<ConfirmationChannel, string> = {
  Phone: 'téléphone', Email: 'email', Sms: 'SMS', Other: 'autre',
}

export function channelLabel(value: ConfirmationChannel): string {
  return CHANNEL_DISPLAY[value]
}

export function ConfirmSlotForm({
  area, onSubmit, onCancel,
}: {
  area: Area
  onSubmit: (channel: ConfirmationChannel, note: string | undefined) => Promise<void>
  onCancel: () => void
}) {
  const [channel, setChannel] = useState<ConfirmationChannel>('Phone')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (busy) return
    setBusy(true)
    try { await onSubmit(channel, note.trim() || undefined) }
    finally { setBusy(false) }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-2" data-testid={`planning-${area}-confirm-form`}>
      <div className="text-[11px] uppercase text-muted-foreground">Confirmation du créneau</div>
      <div className="inline-flex overflow-hidden rounded border text-xs" role="group" aria-label="Canal">
        {CHANNELS.map((c) => (
          <button
            key={c.value} type="button" data-testid={`planning-${area}-confirm-channel-${c.value}`}
            aria-pressed={channel === c.value} onClick={() => setChannel(c.value)}
            className={cn('border-l px-3 py-1 first:border-l-0', channel === c.value ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted')}>
            {c.label}
          </button>
        ))}
      </div>
      <Textarea
        value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="Note (optionnelle)" data-testid={`planning-${area}-confirm-note`}
        className="min-h-0 text-xs" />
      <FormFooter
        busy={busy} onCancel={onCancel} onSubmit={() => void submit()}
        submitLabel="Confirmer la date" submitTestId={`planning-${area}-confirm-submit`}
        cancelTestId={`planning-${area}-confirm-cancel`} />
    </div>
  )
}

// ── Rapport (résumé requis) ─────────────────────────────────────────────────
const reportSchema = z.object({ summary: z.string().trim().min(1, 'Le résumé est requis.') })

/**
 * SOLY-PLAN-006 MS-07 — saving AMENDS instead of overwriting.
 *
 * The endpoint has never guarded against a second call: it overwrites `ReportSummary`, rewrites
 * `ReportSummaryAt` and re-sets Completed. That was harmless only because the path was
 * unreachable — `ReportArea` offered no gesture once a report existed. Décision D3 makes it
 * reachable (a métré can take another créneau after its report, so « Rapport manquant » can come
 * back), and a form opening EMPTY would then erase the first passage's report without a word.
 *
 * Hence `initialSummary`, and only that. It is OPTIONAL and the pose never passes it: the pose
 * never re-opens this form (its second constat is a finition, with its own work item and its own
 * report), so its behaviour is identical to the byte. No `area === 'metre'` branch here — a shared
 * component that knows which caller it serves is how two surfaces start diverging.
 *
 * ⚠️ What the history DOES and DOES NOT keep — measured, not assumed. Every save writes a
 * `SurveyReport` entry in `PlanningAuditEntries`, but `PlanningAuditEntry` TRUNCATES `AfterValue`
 * at 500 characters with a `…[truncated]` marker, and the column is `NVARCHAR(500)` (V089) while
 * `ReportSummary` is `NVARCHAR(MAX)`. So the history keeps the first ~487 characters of each
 * version, not the whole thing. The scoping said « le texte complet »; that is wrong past 500
 * characters and the fiches were corrected.
 *
 * It does not change the decision: with the form pre-filled, losing text now takes actively
 * deleting it, not merely pressing save. But do not sell the audit as a full backup.
 */
export function ReportForm({
  area, initialSummary, onSubmit, onCancel,
}: {
  area: Area
  /** Existing report text, when there is one — the form opens on it and the labels say « Compléter ». */
  initialSummary?: string | null
  onSubmit: (summary: string) => Promise<void>
  onCancel: () => void
}) {
  const amending = Boolean(initialSummary && initialSummary.trim().length > 0)
  const [summary, setSummary] = useState(initialSummary ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (busy) return
    const parsed = reportSchema.safeParse({ summary })
    if (!parsed.success) { setError(parsed.error.issues[0].message); return }
    setError(null); setBusy(true)
    try { await onSubmit(parsed.data.summary) }
    finally { setBusy(false) }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-2" data-testid={`planning-${area}-report-form`}>
      <div className="text-[11px] uppercase text-muted-foreground">{amending ? 'Compléter le rapport' : 'Rapport'}</div>
      <Textarea
        value={summary} onChange={(e) => setSummary(e.target.value)} rows={amending ? 6 : 3} autoFocus
        placeholder="Résumé du rapport…" data-testid={`planning-${area}-report-summary`}
        className="text-xs" />
      {error && <p className="text-[11px] text-red-600" data-testid={`planning-${area}-report-error`}>{error}</p>}
      <FormFooter
        busy={busy} onCancel={onCancel} onSubmit={() => void submit()}
        submitLabel={amending ? 'Compléter le rapport' : 'Enregistrer le rapport'}
        submitTestId={`planning-${area}-report-submit`}
        cancelTestId={`planning-${area}-report-cancel`} />
    </div>
  )
}

// ── Notes de planification (SchedulingNotes, libre, peut être vidé) ─────────
export function NotesForm({
  area, initialNotes, onSubmit, onCancel, title = 'Notes de planification',
  placeholder = 'Notes internes (accès, étage, contraintes…)', maxLength = 1000,
}: {
  area: Area
  initialNotes: string
  onSubmit: (notes: string) => Promise<void>
  onCancel: () => void
  /** Overridden by the créneau note (SOLY-PLAN-004) — same form, two subjects, one implementation. */
  title?: string
  placeholder?: string
  /** Installation.SchedulingNotes holds 1000 chars; a créneau note holds 200 (ScheduleEvents.Label). */
  maxLength?: number
}) {
  const [notes, setNotes] = useState(initialNotes)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (busy) return
    setBusy(true)
    try { await onSubmit(notes.trim()) }
    finally { setBusy(false) }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-2" data-testid={`planning-${area}-notes-form`}>
      <div className="text-[11px] uppercase text-muted-foreground">{title}</div>
      <Textarea
        value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} autoFocus maxLength={maxLength}
        placeholder={placeholder} data-testid={`planning-${area}-notes-text`}
        className="min-h-0 text-xs" />
      <FormFooter
        busy={busy} onCancel={onCancel} onSubmit={() => void submit()}
        submitLabel="Enregistrer" submitTestId={`planning-${area}-notes-submit`}
        cancelTestId={`planning-${area}-notes-cancel`} />
    </div>
  )
}

// ── Raison (NotApplicable / Cancel / Cancel-event), min 5 chars ─────────────
type ReasonAction = 'na' | 'cancel' | 'cancel-event'
const reasonSchema = z.object({ reason: z.string().trim().min(5, 'Raison requise (≥ 5 caractères).') })

const REASON_TITLE: Record<ReasonAction, string> = {
  na: 'Marquer non nécessaire',
  cancel: 'Annuler (définitif)',
  'cancel-event': 'Annuler le rendez-vous',
}

export function ReasonForm({
  action, area, onSubmit, onCancel, notice,
}: {
  action: ReasonAction
  area: Area
  onSubmit: (reason: string) => Promise<void>
  onCancel: () => void
  /**
   * What this particular cancellation COSTS, in plain words, above the reason box (SOLY-PLAN-003
   * MS-11). The convention comes from the kind reclassement of SOLY-PLAN-002: name what is lost,
   * never ask « êtes-vous sûr ? », which tells the user nothing. Omitted → no notice at all, which
   * is what every caller before this one wants.
   */
  notice?: string
}) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (busy) return
    const parsed = reasonSchema.safeParse({ reason })
    if (!parsed.success) { setError(parsed.error.issues[0].message); return }
    setError(null); setBusy(true)
    try { await onSubmit(parsed.data.reason) }
    finally { setBusy(false) }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-2" data-testid={`planning-${area}-${action}-form`}>
      <div className="text-[11px] uppercase text-muted-foreground">{REASON_TITLE[action]}</div>
      {notice && (
        <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
          data-testid={`planning-${area}-${action}-notice`}>
          {notice}
        </p>
      )}
      <Textarea
        value={reason} onChange={(e) => setReason(e.target.value)} rows={2} autoFocus
        placeholder="Raison (≥ 5 caractères)…" data-testid={`planning-${area}-${action}-reason`}
        className="min-h-0 text-xs" />
      {error && <p className="text-[11px] text-red-600" data-testid={`planning-${area}-${action}-error`}>{error}</p>}
      <FormFooter
        busy={busy} onCancel={onCancel} onSubmit={() => void submit()}
        submitLabel="Confirmer" submitTestId={`planning-${area}-${action}-submit`}
        cancelTestId={`planning-${area}-${action}-cancel`} />
    </div>
  )
}

// ── Estimate (Métré heures · Pose jours/heures × placeurs) ──────────────────
const surveyEstimateSchema = z.object({
  // Mirror the BE domain rule (TechnicalSurvey.UpdateEstimate enforces a 0.5h step) so a hand-typed
  // value can't pass FE validation then 400 the API.
  hours: z.number().positive('La durée doit être supérieure à zéro.')
    .refine((h) => Number.isInteger(h / 0.5), 'La durée doit être un multiple de 0,5 h.'),
})
const installEstimateSchema = z.object({
  unit: z.enum(['Hours', 'Days']),
  value: z.number().positive('La durée doit être supérieure à zéro.'),
  resources: z.number().int().min(1, 'Au moins 1 placeur.'),
  // Durée unique (SOLY-PLAN-001) : l'estimation suit la grammaire de l'agenda — jours entiers (1.5j
  // inposable sur le calendrier), heures par pas de 0,5. Mirror BE Installation.UpdateEstimate + event.
}).refine((d) => (d.unit === 'Days' ? Number.isInteger(d.value) : Number.isInteger(d.value / 0.5)), {
  message: 'Jours entiers, ou heures par pas de 0,5.', path: ['value'],
})

type EstimateFormProps =
  | {
      kind: 'survey'
      area: Area
      initialHours: number
      onSubmit: (body: { hours: number; reason?: string }) => Promise<void>
      onCancel: () => void
    }
  | {
      kind: 'installation'
      area: Area
      /** Live RDV, when the work item is scheduled — drives the « la confirmation sautera » heads-up.
       *  Switching unit re-anchors the slot's start (BE ScheduleEvent.ChangeDuration), and a start
       *  change voids the client confirmation. Without this warning Élodie loses a « confirmé »
       *  without seeing it — and that flag is what decides whether the client gets called back. */
      scheduled?: { unit: DurationUnit; confirmedAt: string | null }
      initialUnit: DurationUnit
      /** `null` when the pose is not estimated — the fields then open BLANK (SOLY-PLAN-004). */
      initialValue: number | null
      initialResources: number | null
      onSubmit: (body: { unit: DurationUnit; value: number; resources: number; reason?: string }) => Promise<void>
      onCancel: () => void
    }

export function EstimateForm(props: EstimateFormProps) {
  const { area, onCancel } = props
  const [hours, setHours] = useState(props.kind === 'survey' ? props.initialHours : 0)
  const [unit, setUnit] = useState<DurationUnit>(props.kind === 'installation' ? props.initialUnit : 'Days')
  // '' rather than 0 when nothing is estimated: « pas d'estimation par défaut (nb de jours/heure et
  // nbre de personnes vide) » — Élodie 2026-08-18. A 0 is a value, and it is the one the Zod schema
  // rejects with « la durée doit être supérieure à zéro », so the form would open on its own error.
  const [value, setValue] = useState<number | ''>(props.kind === 'installation' ? props.initialValue ?? '' : 0)
  const [resources, setResources] = useState<number | ''>(props.kind === 'installation' ? props.initialResources ?? '' : 1)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Same wording as the reschedule warning: the cause differs (unit switch vs date move) but the
  // consequence Élodie must anticipate is identical.
  const unitSwitchVoidsConfirmation =
    props.kind === 'installation' && !!props.scheduled?.confirmedAt && props.scheduled.unit !== unit

  const submit = async () => {
    if (busy) return
    const trimmedReason = reason.trim() || undefined
    if (props.kind === 'survey') {
      const parsed = surveyEstimateSchema.safeParse({ hours })
      if (!parsed.success) { setError(parsed.error.issues[0].message); return }
      setError(null); setBusy(true)
      try { await props.onSubmit({ hours: parsed.data.hours, reason: trimmedReason }) }
      finally { setBusy(false) }
    } else {
      // A blank field reaches Zod as NaN rather than 0, so the message is « la durée doit être
      // supérieure à zéro » either way — but nothing is submitted from an empty form by accident.
      const parsed = installEstimateSchema.safeParse({
        unit, value: value === '' ? Number.NaN : value, resources: resources === '' ? Number.NaN : resources,
      })
      if (!parsed.success) { setError(parsed.error.issues[0].message); return }
      setError(null); setBusy(true)
      try { await props.onSubmit({ ...parsed.data, reason: trimmedReason }) }
      finally { setBusy(false) }
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-2" data-testid={`planning-${area}-estimate-form`}>
      <div className="text-[11px] uppercase text-muted-foreground">Modifier l'estimé</div>

      {props.kind === 'survey' ? (
        <label className="block text-xs">
          <span className="text-[11px] uppercase text-muted-foreground">Durée (h)</span>
          <input
            type="number" min={0.5} step={0.5} value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            data-testid={`planning-${area}-estimate-hours`}
            className="mt-0.5 w-full rounded border px-2 py-1" />
        </label>
      ) : (
        <>
          <div className="inline-flex overflow-hidden rounded border text-xs" role="group" aria-label="Unité">
            {(['Hours', 'Days'] as const).map((u) => (
              <button
                key={u} type="button" data-testid={`planning-${area}-estimate-unit-${u}`}
                aria-pressed={unit === u} onClick={() => setUnit(u)}
                className={cn('border-l px-3 py-1 first:border-l-0', unit === u ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted')}>
                {u === 'Hours' ? '⏱ Heures' : '📅 Jours'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <label className="flex-1">
              <span className="text-[11px] uppercase text-muted-foreground">{unit === 'Hours' ? 'Heures' : 'Jours'}</span>
              <input
                type="number" min={unit === 'Days' ? 1 : 0.5} step={unit === 'Days' ? 1 : 0.5} value={value}
                onChange={(e) => setValue(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="—"
                data-testid={`planning-${area}-estimate-value`}
                className="mt-0.5 w-full rounded border px-2 py-1" />
            </label>
            <label className="flex-1">
              <span className="text-[11px] uppercase text-muted-foreground">Placeurs</span>
              <input
                type="number" min={1} step={1} value={resources}
                onChange={(e) => setResources(e.target.value === '' ? '' : Math.round(Number(e.target.value)))}
                placeholder="—"
                data-testid={`planning-${area}-estimate-resources`}
                className="mt-0.5 w-full rounded border px-2 py-1" />
            </label>
          </div>
        </>
      )}

      <label className="block text-xs">
        <span className="text-[11px] uppercase text-muted-foreground">Raison (optionnelle)</span>
        <input
          type="text" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Pourquoi ce changement ?"
          data-testid={`planning-${area}-estimate-reason`}
          className="mt-0.5 w-full rounded border px-2 py-1" />
      </label>

      {unitSwitchVoidsConfirmation && (
        <p className="text-[11px] text-amber-600" data-testid={`planning-${area}-estimate-confirm-warning`}>
          ⚠ Passer en {unit === 'Hours' ? 'heures' : 'jours'} recale l'horaire du rendez-vous et annulera la confirmation client — il faudra la reconfirmer.
        </p>
      )}
      {error && <p className="text-[11px] text-red-600" data-testid={`planning-${area}-estimate-error`}>{error}</p>}
      <FormFooter
        busy={busy} onCancel={onCancel} onSubmit={() => void submit()}
        submitLabel="Enregistrer" submitTestId={`planning-${area}-estimate-submit`}
        cancelTestId={`planning-${area}-estimate-cancel`} />
    </div>
  )
}

// ── Planifier (inline scheduling from the cockpit section) ──────────────────
/** Skill a kind requires (mirrors SchedulingFiche.requiredSkill / BE SchedulingConstraints). */
export function requiredSkill(kind: ScheduleEventKind): EmployeeSkillType | null {
  if (kind === 'TechnicalSurvey') return 'Surveyor'
  if (kind === 'Installation') return 'Installer'
  return null
}


/**
 * Inline scheduling form for a NotScheduled work item, opened straight from the Métré/Pose section
 * (cockpit UX finding #2). Same inline language as the other ProjectDetailForms; mirrors the
 * SchedulingFiche controls (skill-filtered employee chips · date · unit toggle + duration) but submits
 * via createEvent without leaving the drawer. Prefilled from the work item's estimate.
 */
export function ScheduleForm({
  area, kind, projectId, technicalSurveyId, installationId, employees,
  initialUnit, initialValue, onSubmit, onCancel, reschedule, expectedResources,
  initialDayIso, initialAssignees,
}: {
  area: Area
  kind: ScheduleEventKind
  projectId: string
  technicalSurveyId?: string | null
  installationId?: string | null
  employees: EmployeeDto[]
  initialUnit: DurationUnit
  initialValue: number
  onSubmit: (body: {
    kind: ScheduleEventKind
    technicalSurveyId: string | null
    installationId: string | null
    projectId: string
    durationUnit: DurationUnit
    durationValue: number
    assignedUserIds: string[]
  } & SlotStartBody) => Promise<void>
  onCancel: () => void
  /**
   * When set, the form edits an existing event instead of creating one: it prefills date/heure/durée/
   * assignés from the event, asks for an optional reason, and submits via `reschedule.onSubmit`. The
   * BE voids the client confirmation on a start change (V093) — the form warns the user of that.
   */
  reschedule?: {
    event: ScheduleEventDto
    onSubmit: (body: { durationUnit: DurationUnit; durationValue: number; assignedUserIds: string[]; reason?: string } & SlotStartBody) => Promise<void>
  }
  /** Expected resource count (pose placeurs) — shown as a hint so the user assigns the right number (E1). */
  expectedResources?: number
  /**
   * Day to start from (yyyy-MM-dd), or `''` for a DELIBERATELY blank date — SOLY-PLAN-003 MS-11.
   * Adding a second créneau is the case where the date is precisely what is not known yet
   * (scénario S4); defaulting it to today would place work on a day nobody chose. Omitted → today,
   * which is what the first scheduling wants.
   */
  initialDayIso?: string
  /** Crew to start from — a new créneau carries over the previous one's team (décision 5). */
  initialAssignees?: string[]
}) {
  const ev = reschedule?.event
  // Durée unique (SOLY-PLAN-001) : la durée n'est plus éditée ici — elle vient de l'estimation du work
  // item (event existant) ou de l'estimation prefill. Le form ne place que la date + les employés.
  const [unit] = useState<DurationUnit>(ev?.durationUnit ?? initialUnit)
  // SOLY-PLAN-003 MS-09, then SOLY-PLAN-006 MS-02/MS-04 — the slot's duration is editable for BOTH
  // work items now (the estimate stopped being the placed duration on either; see the Durée block
  // below). « Durée unique » is over: this field is the only way left to change a métré's duration.
  const [value, setValue] = useState<number>(ev?.durationValue ?? initialValue)
  const today = new Date().toISOString().slice(0, 10)
  const [dayIso, setDayIso] = useState<string>(ev ? ev.startDate : (initialDayIso ?? today))
  const [startTime, setStartTime] = useState<string>(ev ? startTimeOf(ev) : prefillForKind(kind).startTime)
  const [assignees, setAssignees] = useState<Set<string>>(
    new Set(ev?.assignments.map((a) => a.assignedUserId) ?? initialAssignees ?? []),
  )
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const isReschedule = !!reschedule
  // Compared as (day, time) — SOLY-PLAN-007 — like the backend decides whether a reschedule voids
  // the confirmation. A Days slot has no time, so only its day can move it.
  const next = slotStart(dayIso, unit, startTime)
  const startMoved = !!ev && (next.startDate !== ev.startDate || next.startTime !== ev.startTime)
  const wasConfirmed = !!ev?.confirmedAt

  const skill = requiredSkill(kind)
  // Same order as the agenda lanes — one roster, one order across the screen (see SchedulingFiche).
  const eligible = useMemo(
    () => sortEmployeeLanes(skill ? employees.filter((e) => e.skills.includes(skill)) : employees),
    [employees, skill],
  )

  const toggle = (userId: string) =>
    setAssignees((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId); else next.add(userId)
      return next
    })

  const submit = async () => {
    if (busy) return
    if (value <= 0) { setError('La durée doit être supérieure à zéro.'); return }
    // A blank date is reachable since MS-11 (« + Ajouter un créneau » opens with none on purpose).
    // Same guard as the agenda fiche: without it the empty input becomes a nonsense instant and is
    // sent to the backend.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayIso)) { setError('Choisissez une date pour ce créneau.'); return }
    // Same guard as the agenda fiche: a blank time field, or the midnight anchor the backend
    // refuses on an hourly slot, would reach the API as a nonsense instant.
    if (unit === 'Hours' && !isPlaceableTime(startTime)) { setError('Choisissez une heure de début.'); return }
    if (unit === 'Hours' && spillsPastMidnight(startTime, value)) {
      setError('Un créneau en heures doit se terminer le même jour.'); return
    }
    setError(null); setBusy(true)
    try {
      if (reschedule) {
        await reschedule.onSubmit({
          durationUnit: unit,
          durationValue: value,
          ...slotStart(dayIso, unit, startTime),
          assignedUserIds: [...assignees],
          reason: reason.trim() || undefined,
        })
      } else {
        await onSubmit({
          kind,
          technicalSurveyId: technicalSurveyId ?? null,
          installationId: installationId ?? null,
          projectId,
          durationUnit: unit,
          durationValue: value,
          ...slotStart(dayIso, unit, startTime),
          assignedUserIds: [...assignees],
        })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action refusée')
    } finally { setBusy(false) }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-2" data-testid={`planning-${area}-schedule-form`}>
      <div className="text-[11px] uppercase text-muted-foreground">{isReschedule ? 'Replanifier' : 'Planifier'}</div>

      {/* Employees (skill-filtered) */}
      <div>
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <span className="text-[11px] uppercase text-muted-foreground">Employé(s)</span>
          {expectedResources ? (
            <span
              data-testid={`planning-${area}-schedule-expected`}
              className={cn('text-[11px]', assignees.size === expectedResources ? 'text-muted-foreground' : 'text-amber-600')}>
              {assignees.size}/{expectedResources} estimé{expectedResources > 1 ? 's' : ''}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1" data-testid={`planning-${area}-schedule-employees`}>
          {eligible.length === 0 && <NoEligibleEmployees skill={skill} testId={`planning-${area}-schedule-no-eligible`} />}
          {eligible.map((e) => {
            const on = assignees.has(e.userId)
            return (
              <button
                key={e.userId} type="button" aria-pressed={on}
                data-testid={`planning-${area}-schedule-emp-${e.userId}`}
                onClick={() => toggle(e.userId)}
                className={cn('rounded-full px-2 py-1 text-xs', on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
                {e.displayName || e.email}
              </button>
            )
          })}
        </div>
      </div>

      {/* Date */}
      <label className="block text-xs">
        <span className="text-[11px] uppercase text-muted-foreground">Date</span>
        <input type="date" value={dayIso} onChange={(e) => setDayIso(e.target.value)}
          data-testid={`planning-${area}-schedule-date`} className="mt-0.5 w-full rounded border px-2 py-1" />
      </label>

      {/* Durée du CRÉNEAU, saisissable pour les deux work items.
          Elle était en lecture seule — « durée unique » : l'estimation ÉTAIT la durée placée, et ce
          formulaire posait donc un bloc égal au budget entier. La pose s'en est affranchie en
          SOLY-PLAN-003 MS-05 ; le métré le fait ici (SOLY-PLAN-006 MS-02/MS-04), et c'est
          obligatoire, pas cosmétique : MS-02 retire la propagation de l'estimation vers le créneau,
          donc sans ce champ il n'existerait PLUS AUCUN moyen de changer la durée d'un métré. C'est
          exactement le trou que PLAN-003 a creusé entre MS-05 et MS-08 pour la pose ; on ferme les
          deux dans le même lot.
          Le pas reste 0,5 h (ScheduleEvent.HoursStep côté domaine) et 1 j entier en jours. */}
      {unit === 'Days' ? (
        <label className="block text-xs">
          <span className="text-[11px] uppercase text-muted-foreground">Nombre de jours</span>
          <input type="number" min={1} step={1} value={value}
            onChange={(e) => setValue(Math.max(1, Math.round(Number(e.target.value))))}
            data-testid={`planning-${area}-schedule-days`} className="mt-0.5 w-full rounded border px-2 py-1" />
        </label>
      ) : (
        <label className="block text-xs">
          <span className="text-[11px] uppercase text-muted-foreground">Durée (h)</span>
          <input type="number" min={0.5} step={0.5} value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            data-testid={`planning-${area}-schedule-hours`} className="mt-0.5 w-full rounded border px-2 py-1" />
        </label>
      )}

      {/* Heure de début — seulement pour une durée en heures (ex. métré). Un « jours » démarre à l'ouverture.
          Champ horaire libre depuis SOLY-PLAN-004 : la journée de référence de l'org préremplit et
          renseigne, elle ne borne plus (« un RDV à 18h30 » — Élodie 2026-08-18). */}
      {unit === 'Hours' && (
        <label className="block text-xs">
          <span className="text-[11px] uppercase text-muted-foreground">Heure de début</span>
          <input type="time" step={START_TIME_STEP_SECONDS} value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            data-testid={`planning-${area}-schedule-start`} className="mt-0.5 w-full rounded border px-2 py-1" />
          {isOutsideWorkDay(startTime) && (
            <span className="mt-1 block text-[11px] text-muted-foreground"
              data-testid={`planning-${area}-schedule-outside-workday`}>
              ℹ Hors des heures de travail ({workDayLabel()})
            </span>
          )}
        </label>
      )}

      {/* Reschedule only: optional reason (tracked in the audit log) + heads-up that moving the date
          drops the client confirmation (BE voids it on a start change). */}
      {isReschedule && (
        <Textarea
          value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
          placeholder="Raison du changement (optionnelle)" data-testid={`planning-${area}-reschedule-reason`}
          className="min-h-0 text-xs" />
      )}
      {isReschedule && startMoved && wasConfirmed && (
        <p className="text-[11px] text-amber-600" data-testid={`planning-${area}-reschedule-confirm-warning`}>
          ⚠ Changer la date annulera la confirmation client — il faudra la reconfirmer.
        </p>
      )}

      {error && <p className="text-[11px] text-red-600" data-testid={`planning-${area}-schedule-error`}>{error}</p>}
      <FormFooter
        busy={busy} onCancel={onCancel} onSubmit={() => void submit()}
        submitLabel={isReschedule ? 'Replanifier' : 'Planifier'} submitTestId={`planning-${area}-schedule-submit`}
        cancelTestId={`planning-${area}-schedule-cancel`} />
    </div>
  )
}

// ── Shared footer (Annuler / submit) ────────────────────────────────────────
function FormFooter({
  busy, onCancel, onSubmit, submitLabel, submitTestId, cancelTestId,
}: {
  busy: boolean
  onCancel: () => void
  onSubmit: () => void
  submitLabel: string
  submitTestId: string
  cancelTestId: string
}) {
  return (
    <div className="flex justify-end gap-2">
      <button type="button" onClick={onCancel} data-testid={cancelTestId}
        className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
      <button type="button" onClick={onSubmit} disabled={busy} data-testid={submitTestId}
        className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50">
        {submitLabel}
      </button>
    </div>
  )
}

/**
 * Inline assignee editor (chips + skill-filtered picker).
 *
 * Lives with the other shared forms since SOLY-PLAN-003 MS-11: the métré section, the pose section
 * and now each créneau card open the SAME editor. A twin copy in the card is exactly how one
 * gesture starts behaving differently depending on where it was opened from.
 */
export function AssigneeEditor({ event, kind, employees, userNameById, area, onAssign, onUnassign, onClose }: {
  event: ScheduleEventDto
  kind: ScheduleEventKind
  employees: EmployeeDto[]
  userNameById: Record<string, string>
  area: 'metre' | 'pose'
  onAssign: (uid: string) => void
  onUnassign: (uid: string) => void
  onClose: () => void
}) {
  const skill = requiredSkill(kind)
  const assignedIds = new Set(event.assignments.map((a) => a.assignedUserId))
  const assignable = employees.filter((e) => (!skill || e.skills.includes(skill)) && !assignedIds.has(e.userId))
  return (
    <div className="mt-1 rounded-md border bg-muted/20 p-2" data-testid={`planning-${area}-assign-editor`}>
      <div className="mb-1.5 flex flex-wrap gap-1">
        {event.assignments.length === 0 && <span className="text-xs text-amber-600">aucun</span>}
        {event.assignments.map((a) => (
          <span key={a.assignedUserId} className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs">
            {userNameById[a.assignedUserId] ?? 'Employé'}
            <button type="button" data-testid={`planning-unassign-${a.assignedUserId}`} className="text-muted-foreground hover:text-red-600"
              onClick={() => onUnassign(a.assignedUserId)}>✕</button>
          </span>
        ))}
      </div>
      {assignable.length > 0 ? (
        <select data-testid={`planning-${area}-assign-select`} defaultValue="" className="w-full rounded border px-2 py-1 text-xs"
          onChange={(e) => { const uid = e.target.value; if (uid) onAssign(uid) }}>
          <option value="" disabled>+ Assigner…</option>
          {assignable.map((emp) => <option key={emp.userId} value={emp.userId}>{emp.displayName || emp.email}</option>)}
        </select>
      ) : (
        <p className="text-[11px] text-muted-foreground">Tous les employés compétents sont assignés.</p>
      )}
      <button type="button" onClick={onClose} data-testid={`planning-${area}-assign-close`}
        className="mt-1.5 text-[11px] text-muted-foreground hover:underline">fermer</button>
    </div>
  )
}
